import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Camera } from 'lucide-react';
import { getAvailableActions } from '../services/availableActionsService';
import { ACTIONS_UI } from '../config/actionsConfig';
import { getActionIconGlyph, formatCategoryLabel } from '../utils/actionIcons';
import SocialFollowBanner from './SocialFollowBanner';

function toLogSnapshot(action) {
  return {
    id: action.id,
    actionKey: action.actionKey,
    title: action.title,
    isGenericPrompt: Boolean(action.isGenericPrompt),
    points: action.points ?? 0,
  };
}

function GenericActionCard({
  action,
  customActionText,
  setCustomActionText,
  customIncludePhoto,
  setCustomIncludePhoto,
  customPhotoFile,
  setCustomPhotoFile,
  customPhotoInputKey,
  setCustomPhotoInputKey,
  customPhotoPreviewUrl,
  PhotoPicker,
  onLogAction,
  toLogSnapshot,
}) {
  const customTrimmed = customActionText.trim();
  const customPhotoOk = !customIncludePhoto || customPhotoFile;
  const canLogCustom = customTrimmed.length > 0 && customPhotoOk;
  const icon = getActionIconGlyph(action.iconName);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow border-green-200">
      <div className="flex items-start gap-4">
        <div className="text-3xl">{icon}</div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{action.title}</h3>
              <p className="text-gray-600 text-sm mb-2">{action.description}</p>
              {formatCategoryLabel(action.category) ? (
                <span className="inline-block px-2 py-1 bg-gray-100 text-xs rounded-full text-gray-700">
                  {formatCategoryLabel(action.category)}
                </span>
              ) : null}
            </div>
            <div className="text-right shrink-0 ml-2 max-w-[7rem]">
              <div className="text-xs font-medium text-green-800">Tier</div>
              <div className="text-xs text-gray-500 leading-tight">Last 7 days · when verified</div>
            </div>
          </div>
          <label className="sr-only" htmlFor="custom-climate-action-input">
            Describe your climate action
          </label>
          <textarea
            id="custom-climate-action-input"
            value={customActionText}
            onChange={(e) => setCustomActionText(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Describe what you did…"
            className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y min-h-[4.5rem]"
          />
          <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={customIncludePhoto}
              onChange={(e) => {
                const on = e.target.checked;
                setCustomIncludePhoto(on);
                if (!on) {
                  setCustomPhotoFile(null);
                  setCustomPhotoInputKey((k) => k + 1);
                }
              }}
              className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span>Also upload a photo</span>
          </label>
          {customIncludePhoto && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="custom-climate-action-photo-upload">
                Photo
              </label>
              <PhotoPicker
                idBase="custom-climate-action-photo"
                inputKey={customPhotoInputKey}
                disabled={false}
                onPickFile={(f) => {
                  setCustomPhotoFile(f ?? null);
                  setCustomPhotoInputKey((k) => k + 1);
                }}
              />
              {customPhotoPreviewUrl && (
                <div className="mt-2">
                  <img
                    src={customPhotoPreviewUrl}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              if (!canLogCustom) return;
              const method = customPhotoFile ? 'photo' : 'text';
              onLogAction(toLogSnapshot(action), method, customPhotoFile, customTrimmed);
              setCustomActionText('');
              setCustomIncludePhoto(false);
              setCustomPhotoFile(null);
              setCustomPhotoInputKey((k) => k + 1);
            }}
            disabled={!canLogCustom}
            className="mt-3 w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Camera className="h-4 w-4" />
            Log This Action
          </button>
        </div>
      </div>
    </div>
  );
}

const ActionsView = ({ onLogAction }) => {
  const [actions, setActions] = useState([]);
  const [loadStatus, setLoadStatus] = useState('idle');
  const [loadError, setLoadError] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [customActionText, setCustomActionText] = useState('');
  const [customIncludePhoto, setCustomIncludePhoto] = useState(false);
  const [customPhotoFile, setCustomPhotoFile] = useState(null);
  const [customPhotoInputKey, setCustomPhotoInputKey] = useState(0);

  const customPhotoPreviewUrl = useMemo(
    () => (customPhotoFile ? URL.createObjectURL(customPhotoFile) : null),
    [customPhotoFile]
  );
  useEffect(() => {
    return () => {
      if (customPhotoPreviewUrl) URL.revokeObjectURL(customPhotoPreviewUrl);
    };
  }, [customPhotoPreviewUrl]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadStatus('loading');
      setLoadError('');
      try {
        const rows = await getAvailableActions();
        if (!cancelled) {
          setActions(rows);
          setLoadStatus('success');
        }
      } catch (e) {
        if (!cancelled) {
          setActions([]);
          setLoadStatus('error');
          setLoadError(e?.message || 'Failed to load actions');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const genericAction = useMemo(
    () => actions.find((a) => a.isGenericPrompt) ?? null,
    [actions]
  );
  const standardActions = useMemo(
    () => actions.filter((a) => !a.isGenericPrompt),
    [actions]
  );

  const supportsCameraCapture =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function';

  const CameraCaptureModal = ({ open, onClose, onCapture }) => {
    const [error, setError] = useState('');
    const [stream, setStream] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
      let cancelled = false;

      const start = async () => {
        setError('');
        try {
          const s = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          });
          if (cancelled) {
            s.getTracks().forEach((t) => t.stop());
            return;
          }
          setStream(s);
        } catch (e) {
          setError('Could not access camera. Please allow camera permission or use Upload photo.');
        }
      };

      if (open && supportsCameraCapture) start();

      return () => {
        cancelled = true;
      };
    }, [open]);

    useEffect(() => {
      if (!open && stream) {
        stream.getTracks().forEach((t) => t.stop());
        setStream(null);
      }
      return () => {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      };
    }, [open, stream]);

    useEffect(() => {
      const el = videoRef.current;
      if (el && stream && el.srcObject !== stream) {
        el.srcObject = stream;
      }
    }, [stream]);

    if (!open) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
        <div className="bg-white rounded-2xl p-4 w-full max-w-md">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="font-semibold">Take a photo</div>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
            >
              Close
            </button>
          </div>

          {!supportsCameraCapture && (
            <div className="text-sm text-gray-700">
              Camera capture isn’t supported in this browser. Please use Upload photo.
            </div>
          )}

          {supportsCameraCapture && error && (
            <div className="text-sm text-red-600">{error}</div>
          )}

          {supportsCameraCapture && !error && (
            <div className="space-y-3">
              <div className="w-full overflow-hidden rounded-xl bg-black">
                <video
                  className="w-full h-auto"
                  autoPlay
                  playsInline
                  muted
                  ref={videoRef}
                />
              </div>

              <button
                type="button"
                onClick={async () => {
                  try {
                    const videoEl = videoRef.current;
                    if (!videoEl) return;
                    const w = videoEl.videoWidth || 1280;
                    const h = videoEl.videoHeight || 720;
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;
                    ctx.drawImage(videoEl, 0, 0, w, h);

                    const blob = await new Promise((resolve) =>
                      canvas.toBlob(resolve, 'image/jpeg', 0.9)
                    );
                    if (!blob) return;

                    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                    onCapture(file);
                    onClose();
                  } catch (_e) {
                    setError('Failed to capture photo. Please try again or use Upload photo.');
                  }
                }}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                Use this photo
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const PhotoPicker = ({
    idBase,
    disabled,
    inputKey,
    onPickFile,
  }) => {
    const [showCamera, setShowCamera] = useState(false);

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${idBase}-upload`}>
            Upload photo
          </label>
          <input
            key={`${inputKey}-upload`}
            id={`${idBase}-upload`}
            type="file"
            accept="image/*"
            disabled={disabled}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPickFile(f);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
          />
        </div>

        <div>
          <button
            type="button"
            disabled={disabled || !supportsCameraCapture}
            onClick={() => setShowCamera(true)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg font-medium text-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Take photo (camera)
          </button>
          {!supportsCameraCapture && (
            <div className="text-xs text-gray-500 mt-1">
              Camera not available in this browser. Use Upload photo.
            </div>
          )}
        </div>

        <CameraCaptureModal
          open={showCamera}
          onClose={() => setShowCamera(false)}
          onCapture={onPickFile}
        />
      </div>
    );
  };

  const ActionLogModal = () => {
    const [photoFile, setPhotoFile] = useState(null);
    const [logMethod, setLogMethod] = useState('');
    const [description, setDescription] = useState('');
    const [photoInputKey, setPhotoInputKey] = useState(0);

    const handlePickedPhoto = (file) => {
      setPhotoFile(file);
      setLogMethod('photo');
      setPhotoInputKey((k) => k + 1);
    };

    const trimmedDescription = description.trim();
    const canSubmit =
      logMethod === 'photo'
        ? !!photoFile
        : logMethod === 'text'
          ? trimmedDescription.length > 0
          : false;

    const handleSubmit = () => {
      if (!canSubmit) return;
      const descForLog =
        logMethod === 'text' ? trimmedDescription : trimmedDescription || null;
      onLogAction(
        toLogSnapshot(selectedAction),
        logMethod,
        logMethod === 'photo' ? photoFile : null,
        descForLog
      );
      setShowLogModal(false);
      setSelectedAction(null);
      setPhotoFile(null);
      setDescription('');
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md">
          <h3 className="text-xl font-semibold mb-4">Log Action: {selectedAction?.title}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">How would you like to log this action?</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="photo"
                    checked={logMethod === 'photo'}
                    onChange={(e) => setLogMethod(e.target.value)}
                    className="text-green-600"
                  />
                  <span>Upload photo proof</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="text"
                    checked={logMethod === 'text'}
                    onChange={(e) => setLogMethod(e.target.value)}
                    className="text-green-600"
                  />
                  <span>Write a text description</span>
                </label>
              </div>
            </div>

            {logMethod === 'photo' && (
              <div>
                <PhotoPicker
                  idBase="log-action-photo"
                  inputKey={photoInputKey}
                  disabled={false}
                  onPickFile={handlePickedPhoto}
                />
                {photoFile && (
                  <div className="mt-2">
                    <img 
                      src={URL.createObjectURL(photoFile)} 
                      alt="Preview" 
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700 mt-3 mb-2">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Add any details about this action…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y min-h-[4.5rem]"
                />
              </div>
            )}

            {logMethod === 'text' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Describe what you did…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y min-h-[5rem]"
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {setShowLogModal(false); setSelectedAction(null);}}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Log Action
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <SocialFollowBanner />
      </div>
      <div>
        <h2 className="text-2xl font-bold mb-2">Climate Actions</h2>
        <p className="text-gray-600">
          Log climate actions for review. Verified actions count toward your tier over the last 7 days: Bronze 1+, Silver
          3+, Gold 5+.
        </p>
      </div>

      {loadStatus === 'loading' ? (
        <div className="text-sm text-gray-600">Loading climate actions…</div>
      ) : loadStatus === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{ACTIONS_UI.fetchErrorText}</p>
          {loadError ? <p className="text-xs text-red-700 mt-1">{loadError}</p> : null}
        </div>
      ) : (
        <div className="grid gap-4">
          {genericAction ? (
            <GenericActionCard
              action={genericAction}
              customActionText={customActionText}
              setCustomActionText={setCustomActionText}
              customIncludePhoto={customIncludePhoto}
              setCustomIncludePhoto={setCustomIncludePhoto}
              customPhotoFile={customPhotoFile}
              setCustomPhotoFile={setCustomPhotoFile}
              customPhotoInputKey={customPhotoInputKey}
              setCustomPhotoInputKey={setCustomPhotoInputKey}
              customPhotoPreviewUrl={customPhotoPreviewUrl}
              PhotoPicker={PhotoPicker}
              onLogAction={onLogAction}
              toLogSnapshot={toLogSnapshot}
            />
          ) : null}

          {standardActions.map((action) => {
            const icon = getActionIconGlyph(action.iconName);
            const categoryLabel = formatCategoryLabel(action.category);

            return (
              <div key={action.id} className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{icon}</div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{action.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">{action.description}</p>
                        {categoryLabel ? (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-xs rounded-full text-gray-700">
                            {categoryLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-right shrink-0 ml-2 max-w-[7rem]">
                        <div className="text-xs font-medium text-green-800">Tier</div>
                        <div className="text-xs text-gray-500 leading-tight">Last 7 days · when verified</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAction(action);
                        setShowLogModal(true);
                      }}
                      className="mt-3 w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700"
                    >
                      <Camera className="h-4 w-4" />
                      Log This Action
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!genericAction && standardActions.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-600">{ACTIONS_UI.emptyListText}</p>
            </div>
          ) : null}
        </div>
      )}

      {showLogModal && <ActionLogModal />}
    </div>
  );
};

export default ActionsView;